import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { useTransitionPresence } from "../hooks/useTransitionPresence.js";
import { ControlContext } from "../lib/control-context.js";
import { cn } from "../lib/utils.js";
import { MOTION_DURATION_MS } from "../motion.js";
import { ShellRightRailOccupiedContext } from "../lib/shell-right-rail-context.js";
import { getLayerRoot } from "./layer-stack.js";
import { useDismissable } from "./useDismissable.js";
import { useFocusTrap } from "./useFocusTrap.js";
import { useLayer } from "./useLayer.js";

/* ModalShell — the ONE modal chassis (strategy §14.4, increment 5.3).
   Every Dialog/Drawer renders through it: portal into the layer root,
   visual-only scrim (no handlers — dismissal is useDismissable's job, which
   keeps jsx-a11y honest), modal layer registration (scroll-lock + inert
   background), focus trap with return-focus, z-overlay. Internal — public
   Modal (5.5) will wrap it with an opinionated skin. */

/* Where the panel travels from. The offsets themselves live in theme.css with
   every other overlay origin (ADR-0091), so a dialog here and an anchored menu
   elsewhere cannot drift apart. */
const MOTION_PRESET_ORIGINS = {
  dialog: "pb-motion-origin-dialog",
  "dialog-panel": "pb-motion-origin-dialog-panel",
  "right-sheet": "pb-motion-origin-sheet-end",
  "bottom-sheet": "pb-motion-origin-sheet-bottom",
} as const;

type MotionPreset = keyof typeof MOTION_PRESET_ORIGINS;

export interface ModalShellProps {
  open: boolean;
  onClose: () => void;
  /** Called after the retained exit has finished and the surface is removed. */
  onExited?: (() => void) | undefined;
  /** Accessible name — provide exactly one of label / labelledBy. */
  label?: string;
  labelledBy?: string;
  /** Id of body copy describing the dialog; ignored without panel semantics. */
  describedBy?: string | undefined;
  /** Flex placement of the panel inside the viewport container. */
  containerClassName?: string;
  /** Spatial entrance matching the panel's placement. */
  motionPreset?: MotionPreset;
  /** Scrim look (visual only). */
  scrimClassName?: string;
  /** The role="dialog" panel. */
  panelClassName?: string;
  panelStyle?: React.CSSProperties;
  /** Semantic element for the panel (RightDrawer uses "aside"). */
  panelAs?: "div" | "aside";
  /** When false the wrapper carries no dialog semantics — the child owns
   *  role/aria (UniversalSearchDialog's card is reused inline elsewhere). */
  panelSemantics?: boolean;
  /** Let consumers with their own nested-Esc logic opt out (default true). */
  dismissOnEscape?: boolean;
  dismissOnOutsidePress?: boolean;
  /** Additional elements treated as "inside" for outside-press — for menus
   *  the consumer portals outside the panel (UniversalSearch overflow). */
  extraInsideRefs?: ReadonlyArray<React.RefObject<Element | null>>;
  initialFocusRef?: React.RefObject<HTMLElement | null> | undefined;
  children: React.ReactNode;
}

export function ModalShell({
  open,
  onClose,
  onExited,
  label,
  labelledBy,
  describedBy,
  containerClassName,
  motionPreset = "dialog",
  scrimClassName = "bg-pb-overlay/70 backdrop-blur-sm",
  panelClassName,
  panelStyle,
  panelAs = "div",
  panelSemantics = true,
  dismissOnEscape = true,
  dismissOnOutsidePress = true,
  extraInsideRefs,
  initialFocusRef,
  children,
}: ModalShellProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { present, phase } = useTransitionPresence(
    open,
    MOTION_DURATION_MS.state,
  );
  const transitioningOut = phase === "closing";
  useEffect(() => {
    if (!present) onExited?.();
  }, [onExited, present]);

  // aria-hidden alone leaves retained descendants keyboard-focusable during
  // the exit. Apply inert synchronously before paint and remove it on reopen.
  useLayoutEffect(() => {
    containerRef.current?.toggleAttribute("inert", transitioningOut);
  }, [transitioningOut]);
  const layerId = useLayer({ open, modal: true });

  const insideRefs = useMemo(
    () => [panelRef, ...(extraInsideRefs ?? [])],
    [extraInsideRefs],
  );
  const onDismiss = useCallback(() => onClose(), [onClose]);
  useDismissable({
    layerId,
    open,
    onDismiss,
    insideRefs,
    escape: dismissOnEscape,
    outsidePress: dismissOnOutsidePress,
    preventOutsideFocus: true,
  });
  useFocusTrap(panelRef, { active: open && present, initialFocusRef, layerId });

  if (!present) return null;
  if (typeof document === "undefined") return null;

  const Panel = panelAs;
  const panelOrigin = MOTION_PRESET_ORIGINS[motionPreset];
  const node = (
    <div
      ref={containerRef}
      data-pbds-layer-id={layerId}
      data-pbds-modal-layer=""
      data-motion-state={phase}
      aria-hidden={transitioningOut ? "true" : undefined}
      className={cn(
        "fixed inset-0 z-overlay flex",
        transitioningOut && "pointer-events-none",
        containerClassName,
      )}
    >
      <div
        aria-hidden
        data-motion-state={phase}
        className={cn("absolute inset-0 pb-motion-scrim", scrimClassName)}
      />
      <Panel
        ref={panelRef}
        role={panelSemantics ? "dialog" : undefined}
        aria-modal={panelSemantics ? "true" : undefined}
        aria-label={panelSemantics ? label : undefined}
        aria-labelledby={panelSemantics ? labelledBy : undefined}
        aria-describedby={panelSemantics ? describedBy : undefined}
        data-motion-state={phase}
        className={cn(
          "relative pb-motion-overlay",
          panelOrigin,
          panelClassName,
        )}
        style={panelStyle}
      >
        {/* Portalled out of any rail group: its Buttons keep their own look.
            And out of Shell's side rail: a dialog is sized by the window, so
            a frame inside it never goes compact for the rail (PB-5436). */}
        <ControlContext.Provider value={null}>
          <ShellRightRailOccupiedContext.Provider value={false}>
            {children}
          </ShellRightRailOccupiedContext.Provider>
        </ControlContext.Provider>
      </Panel>
    </div>
  );
  return createPortal(node, getLayerRoot());
}
